import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';

/**
 * Member confirmations for one pool.
 *
 * The commissioner ticks members as confirmed for the week and/or the season.
 * Deliberately says nothing about money — what a tick means is between the
 * commissioner and their pool. Being unconfirmed never blocks anything.
 *
 * Props: poolId, user
 */

const s = {
  wrap: { marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  title: { fontSize: 10, color: '#445', fontWeight: 700, letterSpacing: 1 },
  count: { fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 10,
           background: 'rgba(255,179,71,0.14)', border: '1px solid rgba(255,179,71,0.35)', color: '#ffb347' },
  allGood: { fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 10,
             background: 'rgba(76,175,80,0.14)', border: '1px solid rgba(76,175,80,0.35)', color: '#81c784' },
  row: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7,
         background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 5 },
  name: { flex: 1, minWidth: 0, fontSize: 13, color: '#dde', fontWeight: 600 },
  tick: { padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.05)', color: '#889' },
  tickOn: { background: 'rgba(76,175,80,0.18)', border: '1px solid rgba(76,175,80,0.45)', color: '#81c784' },
  btn: { padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
         border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.06)', color: '#778' },
  btnPrimary: { background: 'linear-gradient(135deg,#013369,#0a5299)', border: 'none', color: '#fff' },
  select: { padding: '5px 8px', background: '#0d1526', border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6, color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' },
  hint: { fontSize: 12, color: '#556', lineHeight: 1.5 },
  mine: { fontSize: 12.5, padding: '8px 12px', borderRadius: 7, marginBottom: 4 },
  mineOk: { background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.25)', color: '#81c784' },
  mineNo: { background: 'rgba(255,179,71,0.08)', border: '1px solid rgba(255,179,71,0.3)', color: '#ffb347' }
};

const MODE_LABEL = { weekly: 'Each week', season: 'Once for the season', both: 'Season + each week' };

export default function ConfirmationsPanel({ poolId, user }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modeDraft, setModeDraft] = useState('weekly');

  const load = useCallback(async () => {
    try {
      const res = await API.get(`/confirmations/${poolId}`);
      setData(res.data);
      if (res.data?.mode) setModeDraft(res.data.mode);
    } catch (e) {
      setData(null);
    }
  }, [poolId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const { enabled, mode, isCommissioner, currentWeek, me, members, outstandingCount } = data;
  // With a weekly cadence and no active week there's nothing to measure — say so
  // rather than showing a green "All confirmed" that isn't true.
  const statusKnown = data.statusKnown !== false;

  // Nothing to show a plain member when the pool doesn't use this.
  if (!enabled && !isCommissioner) return null;

  const setSettings = async (nextEnabled, nextMode) => {
    setBusy(true);
    try {
      await API.put(`/confirmations/${poolId}/settings`, { enabled: nextEnabled, mode: nextMode });
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };

  const toggle = async (userId, scope, isOn) => {
    setBusy(true);
    try {
      if (isOn) {
        await API.delete(`/confirmations/${poolId}`, { params: { user_id: userId, scope } });
      } else {
        await API.post(`/confirmations/${poolId}`, { user_id: userId, scope });
      }
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };

  // ── Not turned on yet (commissioner only sees this) ──
  if (!enabled) {
    return (
      <div style={s.wrap}>
        <div style={s.head}><div style={s.title}>MEMBER CONFIRMATIONS</div></div>
        <div style={{ ...s.hint, marginBottom: 10 }}>
          Track who's squared up with you. You tick each member off; they see a reminder until you do.
          Nothing about it blocks play, and the app never handles the money.
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <select style={s.select} value={modeDraft} onChange={e => setModeDraft(e.target.value)}>
            <option value="weekly">Each week</option>
            <option value="season">Once for the season</option>
            <option value="both">Season + each week</option>
          </select>
          <button style={{ ...s.btn, ...s.btnPrimary }} disabled={busy} onClick={() => setSettings(true, modeDraft)}>
            Turn on
          </button>
        </div>
      </div>
    );
  }

  const showSeason = mode === 'season' || mode === 'both';
  const showWeek = (mode === 'weekly' || mode === 'both') && !!currentWeek;

  return (
    <div style={s.wrap}>
      <div style={s.head}>
        <div style={s.title}>
          MEMBER CONFIRMATIONS · {(MODE_LABEL[mode] || mode).toUpperCase()}
        </div>
        {isCommissioner && (
          !statusKnown
            ? <span style={{ ...s.count, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: '#889' }}>No active week</span>
            : outstandingCount > 0
              ? <span style={s.count}>{outstandingCount} outstanding</span>
              : <span style={s.allGood}>All confirmed</span>
        )}
      </div>

      {/* Your own status */}
      {me && statusKnown && (
        <div style={{ ...s.mine, ...(me.outstanding ? s.mineNo : s.mineOk) }}>
          {me.outstanding
            ? <>You're not confirmed yet{showWeek && currentWeek ? ` for ${currentWeek.label}` : ''}
                {/* Don't tell the commissioner to go check with the commissioner. */}
                {isCommissioner ? '. Tick yourself off below.' : '. Check with your commissioner.'}</>
            : <>You're all set{showWeek && currentWeek ? ` for ${currentWeek.label}` : ''}.</>}
        </div>
      )}
      {!statusKnown && (
        <div style={{ ...s.mine, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#889' }}>
          No active week right now. Confirmations resume when the next week loads.
        </div>
      )}

      {/* Commissioner roster */}
      {isCommissioner && members && (
        <>
          <div style={{ marginTop: 10 }}>
            {members.map(m => (
              <div key={m.user_id} style={s.row}>
                <span style={s.name}>
                  {m.isCommissioner && <span style={{ color: '#ff9999', marginRight: 4 }}>*</span>}
                  {m.username}
                  {m.user_id === user?.id && <span style={{ color: '#7fb3ff', fontSize: 11, marginLeft: 5 }}>(you)</span>}
                </span>
                {showSeason && (
                  <button
                    title={m.season ? 'Confirmed for the season, click to undo' : 'Mark confirmed for the season'}
                    style={{ ...s.tick, ...(m.season ? s.tickOn : {}) }}
                    disabled={busy}
                    onClick={() => toggle(m.user_id, 'season', m.season)}
                  >{m.season ? '✓ Season' : 'Season'}</button>
                )}
                {showWeek && (
                  <button
                    title={m.week ? 'Confirmed for this week, click to undo' : 'Mark confirmed for this week'}
                    style={{ ...s.tick, ...(m.week ? s.tickOn : {}) }}
                    disabled={busy}
                    onClick={() => toggle(m.user_id, 'week', m.week)}
                  >{m.week ? '✓ Week' : 'Week'}</button>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
            <select style={s.select} value={modeDraft} onChange={e => setModeDraft(e.target.value)}>
              <option value="weekly">Each week</option>
              <option value="season">Once for the season</option>
              <option value="both">Season + each week</option>
            </select>
            {modeDraft !== mode && (
              <button style={s.btn} disabled={busy} onClick={() => setSettings(true, modeDraft)}>Save</button>
            )}
            <button style={s.btn} disabled={busy} onClick={() => setSettings(false, mode)}>Turn off</button>
          </div>
        </>
      )}
    </div>
  );
}
